import { useEffect, useRef } from 'react';

type CloseFn = () => void;

/** Last registered = topmost modal. Esc closes only the top. */
const closeStack: CloseFn[] = [];
let listenerAttached = false;

function ensureGlobalListener() {
  if (listenerAttached || typeof window === 'undefined') return;
  listenerAttached = true;
  window.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape' && event.code !== 'Escape') return;
      if (closeStack.length === 0) return;
      event.preventDefault();
      event.stopPropagation();
      closeStack[closeStack.length - 1]();
    },
    true,
  );
}

/**
 * Register a modal/dialog so Esc closes it.
 * Nested modals: last mounted (topmost) closes first.
 */
export function useEscapeToClose(onClose: CloseFn, enabled = true) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!enabled) return;
    ensureGlobalListener();
    const wrapper: CloseFn = () => onCloseRef.current();
    closeStack.push(wrapper);
    return () => {
      const idx = closeStack.lastIndexOf(wrapper);
      if (idx >= 0) closeStack.splice(idx, 1);
    };
  }, [enabled]);
}
